import {
    type BackgroundColourStyle,
    type BorderColourStyle,
    type ColourDepth,
    type InputStyleSheet,
    type TextColourStyle,
    Theme,
    type ThemeColours,
} from "@qendo/theme";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";
import type { MD3Theme } from "react-native-paper";
import { base } from "~/constants/colours";
import { PreferenceKey, preferenceStorage } from "~/util/secure-storage";
import { makeInputStyles, makePaperTheme, makeStyleSheets, ThemeDarkMap, ThemeMap } from "~/util/theme";

const DefaultTheme = Theme.light_blue;
type CycleColourKey = Extract<ColourDepth, 0 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900>;

declare interface ThemeState {
    theme: Theme;
    dark: boolean;
    changeTheme: (value: Theme) => void;
    colours: ThemeColours;
    cycle_colours: Record<CycleColourKey, string>;
    inputs: InputStyleSheet;
    text: TextColourStyle;
    background: BackgroundColourStyle;
    border: BorderColourStyle;
    paperTheme: MD3Theme;
}

const default_styles = makeStyleSheets(ThemeMap[DefaultTheme]);

export const ThemeContext = createContext<ThemeState>({
    theme: DefaultTheme,
    dark: false,
    changeTheme: (_value) => {
        return;
    },
    colours: ThemeMap[DefaultTheme],
    cycle_colours: base.blue,
    inputs: makeInputStyles(ThemeMap[DefaultTheme]),
    paperTheme: makePaperTheme(DefaultTheme),
    ...default_styles,
});

export function useTheme() {
    const value = useContext(ThemeContext);
    if (!value) {
        throw new Error("useTheme must be wrapped in a <ThemeProvider />");
    }
    return value;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
    const [theme, setTheme] = useState<Theme>(DefaultTheme);

    useEffect(() => {
        preferenceStorage.getItem<Theme>(PreferenceKey.theme).then((value) => setTheme(value ?? DefaultTheme));
    }, []);

    const changeTheme = (theme: Theme) => {
        preferenceStorage.setItem(PreferenceKey.theme, theme);
        setTheme(theme);
    };

    const dark = useMemo(() => ThemeDarkMap[theme], [theme]);
    const colours = ThemeMap[theme];
    const style_sheets = useMemo(() => makeStyleSheets(colours), [colours]);
    const cycle_colours = useMemo(() => {
        switch (theme) {
            case Theme.dark_pink:
                return base["pink-inverse"];
            case Theme.dark_blue:
                return base["navy-inverse"];
            case Theme.light_pink:
                return base.pink;
            default:
                return base.blue;
        }
    }, [theme]);
    const inputs = useMemo(() => makeInputStyles(colours), [colours]);
    const paperTheme = useMemo(() => makePaperTheme(theme), [theme]);

    const value = {
        theme,
        dark,
        changeTheme,
        colours,
        cycle_colours,
        inputs,
        paperTheme,
        ...style_sheets,
    };

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
